/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DeviceStandalone from "../components/device/DeviceStandalone";

const API = process.env.REACT_APP_API_URL || "/api";

function DevicePage() {
  const { uid }  = useParams();   // matches <Route path="devices/:uid" />
  const navigate = useNavigate();
  const token    = localStorage.getItem("token");

  const [device,  setDevice]  = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  const fetchDevice = async () => {
    try {
      setLoading(true);
      setError("");

      if (!token) throw new Error("Not authenticated");
      if (!uid)   throw new Error("No device UID provided");

      const res = await fetch(`${API}/devices/uid/${encodeURIComponent(uid)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.status === 404) throw new Error("Device not found");
      if (res.status === 403) throw new Error("Access denied");
      if (!res.ok)            throw new Error("Failed to load device");

      const data = await res.json();

      /*
       * mergeDeviceData in backend attaches:
       *   .sensors         → product sensor configs (key, label, unit, group_type)
       *   .relays          → product relay configs
       *   .last_dynamo_data → last known live reading (seeds initial liveData)
       *
       * Handle both flat and nested { device, sensors, relays } shapes
       */
      const finalDevice = data.device
        ? {
            ...data.device,
            sensors:          data.sensors           || [],
            relays:           data.relays            || [],
            last_dynamo_data: data.last_dynamo_data  || {},
          }
        : {
            ...data,
            sensors:          data.sensors           || [],
            relays:           data.relays            || [],
            last_dynamo_data: data.last_dynamo_data  || {},
          };

      setDevice(finalDevice);

    } catch (err) {
      console.error("Device fetch error:", err);
      setError(err.message || "Failed to load device");
    } finally {
      setLoading(false);
    }
  };

  // Refetch only when the route UID changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDevice(); }, [uid]);

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 rounded-full border-[3px] border-gray-200 border-t-blue-600 animate-spin" />
          <p className="text-[13px] text-gray-400 font-medium">Loading device…</p>
        </div>
      </div>
    );
  }

  /* ── Error ── */
  if (error) {
    return (
      <div className="flex items-center justify-center h-64 p-6">
        <div className="bg-white border border-gray-200 rounded-xl p-8 max-w-sm w-full text-center space-y-4">
          <p className="text-[14px] font-semibold text-red-600">{error}</p>
          <div className="flex gap-2 justify-center">
            <button onClick={fetchDevice}
              className="px-4 py-2 text-[13px] font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors">
              Retry
            </button>
            <button onClick={() => navigate("/dashboard/devices")}
              className="px-4 py-2 text-[13px] font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors">
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!device) return null;

  /*
   * DeviceStandalone owns:
   *   - AWS WebSocket connection (real IoT data)
   *   - liveData state
   *   - tab navigation
   *   - relay control
   *
   * DevicePage only owns:
   *   - fetching static device info from DB
   *   - passing device (with sensors + relays) down
   */
  return <DeviceStandalone device={device} onBack={() => navigate("/dashboard/devices")} />;
}

export default DevicePage;
